import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Truck, Send, Loader2, CheckCircle2, Copy, Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';
import DispatchPreviewDialog from '@/components/DispatchPreviewDialog';

/**
 * Delivery dispatch panel for the POS Order detail view.
 * Shows driver assignment status and lets the cashier dispatch the order
 * to drivers via the shared DispatchPreviewDialog (preview + confirm).
 */
export function DeliveryDispatchPanel({ order, onChanged }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/delivery/${order.id}/dispatch-status`);
      setInfo(data);
    } catch (e) {
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [order?.id]);
  useEffect(() => { load(); }, [load]);

  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success('Copied'); } catch { /* ignore */ }
  };

  const claimForDriver = async (driverId, driverName) => {
    if (!info?.dispatch?.token) return;
    setClaimingId(driverId);
    try {
      await api.post(`/delivery/dispatch/${info.dispatch.token}/claim-for-driver`, null, {
        params: { driver_id: driverId },
      });
      toast.success(`Claimed for ${driverName}`);
      await load();
      onChanged?.();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast.error('Already accepted by another driver');
      else if (status === 410) toast.error('Dispatch no longer available');
      else toast.error(e?.response?.data?.detail || 'Failed to claim');
      await load();
    } finally {
      setClaimingId(null);
    }
  };

  const d = info?.dispatch;
  const acceptedDriver = info?.accepted_driver;
  const hasDriver = !!order?.driver_id;
  const inProgress = d && d.status === 'open';

  return (
    <div data-testid="pos-dispatch-panel" className="rounded-lg border border-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-semibold">
          <Truck className="h-4 w-4 text-primary" /> Driver dispatch
        </p>
        {loading ? (
          <Badge variant="secondary"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Checking…</Badge>
        ) : hasDriver ? (
          <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-900">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Assigned
          </Badge>
        ) : inProgress ? (
          <Badge className="border border-amber-200 bg-amber-50 text-amber-900">
            <Clock className="mr-1 h-3 w-3" /> Dispatched · waiting
          </Badge>
        ) : (
          <Badge variant="secondary">No driver yet</Badge>
        )}
      </div>

      {hasDriver && acceptedDriver && (
        <p data-testid="pos-dispatch-assigned" className="mt-1 text-xs text-muted-foreground">
          {acceptedDriver.full_name} · {acceptedDriver.phone || '—'}
        </p>
      )}

      {!hasDriver && d && (
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          <p>Strategy: <strong className="capitalize">{d.strategy}</strong> · {d.candidate_ids?.length || 0} candidate(s)</p>
          {d.status === 'open' && d.expires_at && (
            <p>Expires {new Date(d.expires_at).toLocaleTimeString()}</p>
          )}
          {d.status === 'expired' && <p className="text-rose-700">Expired — dispatch again to retry.</p>}
        </div>
      )}

      {/* Candidate list with "Claim for driver" \u2014 SMS-fallback when a driver calls
          the restaurant instead of tapping the link. */}
      {!hasDriver && info?.candidates?.length > 0 && d?.status === 'open' && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs">
          <p className="mb-2 flex items-center gap-1 font-semibold text-amber-900">
            <Phone className="h-3.5 w-3.5" /> If a driver calls in (no internet), claim for them:
          </p>
          <ul className="space-y-1">
            {info.candidates.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{c.full_name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{c.phone || '\u2014'}{c.city ? ` \u00b7 ${c.city}` : ''}</p>
                </div>
                <Button
                  data-testid={`pos-dispatch-claim-for-${c.id}`}
                  size="sm"
                  variant="secondary"
                  onClick={() => claimForDriver(c.id, c.full_name)}
                  disabled={claimingId === c.id}
                >
                  {claimingId === c.id ? 'Claiming…' : 'Claim'}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lastResult?.accept_url && (
        <button
          type="button"
          onClick={() => copy(lastResult.accept_url)}
          className="mt-2 flex w-full items-center gap-1 rounded bg-muted px-2 py-1 text-left font-mono text-[10px] text-muted-foreground hover:bg-muted/70"
          data-testid="pos-dispatch-link"
        >
          <Copy className="h-3 w-3" /> {lastResult.accept_url}
        </button>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {!hasDriver && (
          <Button data-testid="pos-dispatch-button" size="sm" onClick={() => setOpen(true)}>
            <Send className="mr-1 h-4 w-4" /> {inProgress ? 'Re-dispatch' : 'Dispatch to drivers'}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={load}>Refresh</Button>
      </div>

      <DispatchPreviewDialog
        open={open}
        onOpenChange={setOpen}
        order={order}
        onDispatched={(data) => { setLastResult(data); load(); onChanged?.(); }}
      />
    </div>
  );
}
